// Runtime configuration.
//
// Precedence: runtime /config.js (written by the container at startup) → Vite
// build-time env (local `npm run dev`) → a safe default. One Docker image
// deploys to every environment; the difference is the K8s ConfigMap.
//
// NOTE: with the BFF auth model there is NO OAuth config in the front-end — no
// client_id, no secret, no token. portal-api owns the OIDC flow and the session
// cookie. See CLAUDE.md → Authentication.

export type PortalConfig = {
  /** portal-api base path. Same-origin by default ('/api') so the session
   *  cookie is first-party and no CORS is needed. */
  apiBaseUrl: string;
  /** Public Sentry DSN (optional). NOT a secret. */
  sentryDsn: string;
  /** 'production' | 'internal' | 'development' */
  appEnv: string;
  /** Build/deploy version string */
  appVersion: string;
};

// Raw shape written onto window by docker/40-render-config.sh (UPPER_SNAKE keys).
type RawConfig = {
  API_BASE_URL?: string;
  SENTRY_DSN?: string;
  APP_ENV?: string;
  APP_VERSION?: string;
};

declare global {
  interface Window {
    __PORTAL_CONFIG__?: RawConfig;
  }
}

const runtime: RawConfig =
  (typeof window !== 'undefined' && window.__PORTAL_CONFIG__) || {};

function pick(
  runtimeVal: string | undefined,
  envVal: string | undefined,
  fallback: string,
): string {
  if (runtimeVal && runtimeVal.length > 0) return runtimeVal;
  if (envVal && envVal.length > 0) return envVal;
  return fallback;
}

export const config: PortalConfig = {
  apiBaseUrl: pick(runtime.API_BASE_URL, import.meta.env.VITE_API_BASE_URL, '/api'),
  sentryDsn: pick(runtime.SENTRY_DSN, import.meta.env.VITE_SENTRY_DSN, ''),
  appEnv: pick(runtime.APP_ENV, import.meta.env.VITE_APP_ENV, 'development'),
  appVersion: pick(runtime.APP_VERSION, import.meta.env.VITE_APP_VERSION, 'dev'),
};
